import { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import FloorSection from "./FloorSection";
import RoofSection from "./RoofSection";
import WallSection from "./WallSection";

export default function QuoteSection(props) {
  const [selectedSections, setSelectedSections] = useState([]);
  const [openModal, setOpenModal] = useState(null);
  const [isFloorCompleted, setIsFloorCompleted] = useState(false);
  const [isRoofCompleted, setRoofCompleted] = useState(false);
  const [isWallComplete, setWallCompleted] = useState(false);
  const [additionFloorFieldsCompleted, setAdditionFloorFieldsCompleted] =
    useState(false);

  const [floorsSelectedForQuote, setFloorsSelectedForQuote] = useState(false);

  const isFloorsRequired = () =>
    floorsSelectedForQuote ||
    selectedSections.includes("Roofs") ||
    selectedSections.includes("Wall Panels");

  // Floors are only in the list to support a roof quote — the bearing info
  // (levels/materials) is all that's needed, not the additional floor fields.
  const floorsForRoofOnly =
    !floorsSelectedForQuote &&
    selectedSections.includes("Roofs") &&
    !selectedSections.includes("Wall Panels");

  const sections = [
    { label: "Roofs", key: "roofs" },
    { label: "Wall Panels", key: "walls" },
    { label: "Floors", key: "floors" },
  ];

  const sectionDescriptions = {
    Roofs: "Truss, loading, and roofing details.",
    "Wall Panels": "Wall heights, widths, and sheathing.",
    Floors: "Floor system for each level of the building.",
  };

  const getSectionStatus = (label) => {
    if (label === "Floors")
      return isFloorCompleted &&
        (floorsForRoofOnly || additionFloorFieldsCompleted)
        ? "Completed"
        : "Incomplete";
    if (label === "Roofs") return isRoofCompleted ? "Completed" : "Incomplete";
    if (label === "Wall Panels")
      return isWallComplete ? "Completed" : "Incomplete";

    return "In Progress";
  };

  // Report selection + completion up to the parent so it can gate submit
  useEffect(() => {
    props.onProgressChange?.(
      selectedSections.map((label) => ({
        label,
        completed: getSectionStatus(label) === "Completed",
      }))
    );
  }, [
    selectedSections,
    floorsSelectedForQuote,
    isFloorCompleted,
    isRoofCompleted,
    isWallComplete,
    additionFloorFieldsCompleted,
  ]);

  const toggleSection = (label) => {
    if (label === "Floors") {
      setFloorsSelectedForQuote((prev) => !prev);
    } else {
      setSelectedSections((prev) =>
        prev.includes(label)
          ? prev.filter((item) => item !== label)
          : [...prev, label]
      );
    }
  };

  // Restore a saved product selection (e.g. when a template is applied)
  useEffect(() => {
    if (!props.applySelection) return;
    const products = props.applySelection.products || [];
    setFloorsSelectedForQuote(products.includes("Floors"));
    setSelectedSections(products.filter((p) => p !== "Floors"));
  }, [props.applySelection?.seq]);

  useEffect(() => {
    if (isFloorsRequired()) {
      setSelectedSections((prev) =>
        prev.includes("Floors") ? prev : [...prev, "Floors"]
      );
    } else {
      setSelectedSections((prev) => prev.filter((item) => item !== "Floors"));
    }
  }, [selectedSections, floorsSelectedForQuote]);

  const renderSectionComponent = () => {
    if (openModal === "Roofs")
      return (
        <RoofSection
          config={props.config}
          isComplete={isRoofCompleted}
          setIsComplete={setRoofCompleted}
          formValues={props.roofInfo}
          setFormValues={props.setRoofInfo}
        />
      );
    if (openModal === "Wall Panels")
      return (
        <WallSection
          config={props.config}
          isComplete={isWallComplete}
          setIsComplete={setWallCompleted}
          formValues={props.wallInfo}
          setFormValues={props.setWallInfo}
        />
      );
    if (openModal === "Floors")
      return (
        <FloorSection
          config={props.config}
          isComplete={isFloorCompleted}
          setIsComplete={setIsFloorCompleted}
          additionFieldsCompleted={additionFloorFieldsCompleted}
          setAdditionFloorFieldsCompleted={setAdditionFloorFieldsCompleted}
          levels={props.levels}
          setLevels={props.setLevels}
          formValues={props.addFloorInfo}
          setFormValues={props.setAddFloorInfo}
          quoteRequired={floorsSelectedForQuote}
          basicOnly={floorsForRoofOnly}
        />
      );
    return null;
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {sections.map(({ label }) => (
          <Button
            key={label}
            startIcon={
              (label === "Floors"
                ? floorsSelectedForQuote
                : selectedSections.includes(label)) ? (
                <CheckIcon />
              ) : (
                <AddIcon />
              )
            }
            variant={
              label === "Floors"
                ? floorsSelectedForQuote
                  ? "contained"
                  : "outlined"
                : selectedSections.includes(label)
                ? "contained"
                : "outlined"
            }
            onClick={() => toggleSection(label)}
          >
            {label}
          </Button>
        ))}
      </div>

      {selectedSections.length === 0 && (
        <p className="app-muted mt-4 text-sm">
          Nothing selected yet — choose a product above to get started.
        </p>
      )}

      {selectedSections.length > 0 && (
        <div className="app-surface mt-5 overflow-hidden rounded-xl border">
          <h3 className="px-4 pb-1 pt-3 text-sm font-semibold">To-Do List</h3>
          <ul className="divide-y divide-[color:var(--app-divider)]">
            {selectedSections.map((label) => (
              <li
                key={label}
                className="flex items-center justify-between gap-2 px-2 py-1.5 sm:px-3"
              >
                <button
                  type="button"
                  className="flex min-h-[2.5rem] min-w-0 flex-1 flex-wrap items-center gap-2 rounded-lg px-2 text-left transition-colors hover:bg-[color:var(--app-hover)]"
                  onClick={() => setOpenModal(label)}
                >
                  <span className="app-accent font-medium">{label}</span>
                  <Chip
                    label={getSectionStatus(label)}
                    size="small"
                    color={
                      getSectionStatus(label) === "Completed"
                        ? "success"
                        : getSectionStatus(label) === "Incomplete"
                        ? "warning"
                        : "default"
                    }
                  />
                  {label === "Floors" && floorsForRoofOnly && (
                    <span className="app-muted text-xs">
                      (bearing info only — needed for roof quote)
                    </span>
                  )}
                  <ChevronRightIcon
                    fontSize="small"
                    className="app-muted ml-auto"
                  />
                </button>
                <IconButton
                  size="small"
                  onClick={() => toggleSection(label)}
                  aria-label={`Remove ${label}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal open={!!openModal} onClose={() => setOpenModal(null)}>
        <Box className="app-glass app-glass-rim absolute left-1/2 top-1/2 flex max-h-[90vh] w-[94vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl border outline-none">
          <div className="app-divider flex items-start justify-between gap-3 border-b px-5 py-4 sm:px-6">
            <div>
              <div className="flex items-center gap-3">
                <Typography variant="h6">{openModal}</Typography>
                {openModal && (
                  <Chip
                    label={getSectionStatus(openModal)}
                    size="small"
                    color={
                      getSectionStatus(openModal) === "Completed"
                        ? "success"
                        : "warning"
                    }
                  />
                )}
              </div>
              <p className="app-muted mt-0.5 text-sm">
                {sectionDescriptions[openModal]}
              </p>
            </div>
            <IconButton onClick={() => setOpenModal(null)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            {renderSectionComponent()}
          </div>
          <div className="app-divider flex items-center justify-between gap-3 border-t px-5 py-3 sm:px-6">
            {openModal && getSectionStatus(openModal) === "Completed" ? (
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                ✓ All required fields are filled.
              </p>
            ) : (
              <p className="app-muted text-sm">
                Complete the remaining required fields to finish this section.
              </p>
            )}
            <Button variant="contained" onClick={() => setOpenModal(null)}>
              Done
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
